export interface I18nTemplateMatcher {
  hash: string
  regex: RegExp
}

const RICH_TAG_RE = /<\/?[A-Za-z][A-Za-z0-9_.-]*>/g
// Tokens that render as arbitrary runtime content, so they become `.+?`
// wildcards in the matcher: ICU `{var}` placeholders and *empty* rich-tag pairs
// like `<code1></code1>` whose content is supplied by a `t.rich` render function
// (e.g. an injected URL). A non-empty pair such as `<bold>{name}</bold>` is NOT
// matched here — its `{name}` is captured as a wildcard and the tags are stripped
// to literal text by `toVisibleI18nText` on the surrounding chunk.
const TEMPLATE_TOKEN_RE = /\{[A-Za-z_][A-Za-z0-9_.-]*\}|<([A-Za-z][A-Za-z0-9_.-]*)>\s*<\/\1>/g

function escapeRegExp(input: string): string {
  return input.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

function escapeLiteralChunk(input: string): string {
  return escapeRegExp(input).replace(/\s+/g, '\\s+')
}

export function compileSimpleI18nTemplate(value: string): RegExp | null {
  TEMPLATE_TOKEN_RE.lastIndex = 0
  const matches = Array.from(value.matchAll(TEMPLATE_TOKEN_RE))
  if (matches.length === 0) return null

  // Interleave rich-tag-stripped literal chunks with `.+?` wildcards for each
  // token. Literal length is summed across the stripped chunks so a template
  // that is *only* wildcards (e.g. `{name}`) is rejected — it would match
  // virtually any rendered text.
  let pattern = '^'
  let cursor = 0
  let literalLength = 0
  for (const match of matches) {
    const index = match.index ?? 0
    const literal = toVisibleI18nText(value.slice(cursor, index))
    literalLength += literal.trim().length
    pattern += escapeLiteralChunk(literal)
    pattern += '.+?'
    cursor = index + match[0].length
  }
  const tail = toVisibleI18nText(value.slice(cursor))
  literalLength += tail.trim().length
  pattern += escapeLiteralChunk(tail)
  pattern += '$'

  if (literalLength < 2) return null
  return new RegExp(pattern, 'u')
}

export function toVisibleI18nText(value: string): string {
  return value.replace(RICH_TAG_RE, '')
}

export function addI18nTemplateMatcher(
  matchers: I18nTemplateMatcher[],
  seen: Set<string>,
  value: string | undefined,
  hash: string,
) {
  if (!value) return
  const regex = compileSimpleI18nTemplate(value)
  if (!regex) return
  const key = `${hash}\u0000${regex.source}`
  if (seen.has(key)) return
  seen.add(key)
  matchers.push({ hash, regex })
}

export function findI18nTemplateMatches(value: string, matchers: readonly I18nTemplateMatcher[]): string[] | undefined {
  let hashes: string[] | undefined
  for (const matcher of matchers) {
    if (!matcher.regex.test(value)) continue
    if (!hashes) hashes = []
    if (!hashes.includes(matcher.hash)) hashes.push(matcher.hash)
  }
  return hashes
}
